import clsx from 'clsx';
import * as React from 'react';

import { Icon, type SVGIconComponent } from '@gitbook/spine-icons';

import { IllustrationCard } from '../primitives/IllustrationCard';
import { IllustrationFrame } from '../primitives/IllustrationFrame';
import { WireframeBar } from '../primitives/WireframeBar';

type ImportSource = 'files' | 'website';

export type ImportSourceIllustrationProps = React.ComponentPropsWithoutRef<'div'> & {
    /** Selects which import source this illustrates: local files or a public website. */
    source: ImportSource;
};

/**
 * Mid-fidelity illustration for the import source cards.
 * `source="files"` shows an upload list of the supported file formats feeding
 * into a GitBook page; `source="website"` shows a public docs site in a
 * browser window with the resulting GitBook page emerging in front.
 * Empty-data vocabulary — contrast with `Skeleton` (loading vocabulary).
 */
export function ImportSourceIllustration({
    ref,
    ...props
}: ImportSourceIllustrationProps & { ref?: React.Ref<HTMLDivElement> }) {
    const { source, className, ...rest } = props;

    return (
        <div
            ref={ref}
            aria-hidden
            {...rest}
            className={clsx('mx-auto flex w-full items-center justify-center', className)}
        >
            <IllustrationFrame width={240} height={104}>
                {source === 'files' ? <FilesComposition /> : <WebsiteComposition />}
            </IllustrationFrame>
        </div>
    );
}

/** Supported upload formats, rendered as a staggered file list tucking into the page. */
const FILE_ROWS: { icon: SVGIconComponent; label: string; offset: string; barWidth: string }[] = [
    { icon: Icon.FileText, label: '.md', offset: 'top-0 left-[10px]', barWidth: 'w-10' },
    { icon: Icon.FileText, label: '.docx', offset: 'top-[27px] left-0', barWidth: 'w-7' },
    { icon: Icon.FileCode, label: '.html', offset: 'top-[54px] left-[12px]', barWidth: 'w-8' },
    { icon: Icon.FileZIP, label: '.zip', offset: 'top-[81px] left-[4px]', barWidth: 'w-9' },
];

/** Upload list of supported formats sliding into the GitBook page. */
function FilesComposition() {
    return (
        <div className="relative h-[104px] w-[240px] shrink-0">
            {FILE_ROWS.map(({ icon: RowIcon, label, offset, barWidth }) => (
                <div
                    key={label}
                    className={clsx(
                        'absolute flex h-[22px] w-[132px] items-center gap-1.5 rounded-md border border-neutral-subtle bg-base px-2 shadow-subtle',
                        offset
                    )}
                >
                    <RowIcon className="size-2.5 shrink-0 text-neutral-low" />
                    <span className="text-[0.5rem] leading-none font-bold text-neutral-low uppercase">
                        {label}
                    </span>
                    <WireframeBar className={clsx('ml-auto h-1 opacity-60', barWidth)} />
                </div>
            ))}
            <GitBookPageCard className="absolute top-[11px] right-0 rotate-1" />
        </div>
    );
}

/** Public docs site in a browser window, with the GitBook page emerging in front. */
function WebsiteComposition() {
    return (
        <div className="relative h-[104px] w-[240px] shrink-0">
            <IllustrationCard className="absolute top-0 left-0 w-[150px]">
                <div className="flex items-center gap-1 border-b border-neutral-subtle bg-neutral-surface-subtle px-2 py-1.5">
                    <span className="size-1 rounded-full bg-neutral-ui" />
                    <span className="size-1 rounded-full bg-neutral-ui" />
                    <span className="size-1 rounded-full bg-neutral-ui" />
                    <span className="ml-auto flex h-3.5 items-center gap-1 rounded-full bg-neutral-ui px-1.5">
                        <Icon.Globe className="size-2 text-neutral-low" />
                        <WireframeBar className="h-1 w-10 bg-neutral-ui-active opacity-70" />
                    </span>
                </div>
                <div className="flex gap-2 p-2">
                    <div className="flex w-5 flex-col gap-1 pt-0.5">
                        <WireframeBar className="h-1 w-full" />
                        <WireframeBar className="h-1 w-4/5 opacity-60" />
                        <WireframeBar className="h-1 w-full opacity-60" />
                        <WireframeBar className="h-1 w-3/5 opacity-40" />
                    </div>
                    <div className="flex flex-1 flex-col gap-1 border-l border-neutral-subtle pl-2">
                        <WireframeBar className="h-1.5 w-2/3" />
                        <WireframeBar className="h-1 w-full opacity-60" />
                        <WireframeBar className="h-1 w-4/5 opacity-60" />
                        <WireframeBar className="h-1 w-full opacity-40" />
                        <WireframeBar className="h-1 w-1/2 opacity-40" />
                    </div>
                </div>
            </IllustrationCard>
            <GitBookPageCard className="absolute top-[11px] right-0 rotate-1" />
            {/* Rendered after the card so the arrow visibly lands inside it. */}
            <svg
                aria-hidden
                className="absolute top-[43px] left-[80px]"
                viewBox="0 0 78 40"
                width={78}
                height={40}
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M5 25c15 12 40 9 57-5"
                    stroke="var(--primary-solid)"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    strokeDasharray="0.1 4.5"
                />
                <path
                    d="M65.5 15.4 70 15l-1.2 4.3"
                    stroke="var(--primary-solid)"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </div>
    );
}

/** Destination GitBook page — shared by both sources so the pair reads as "same place". */
function GitBookPageCard(props: { className?: string }) {
    const { className } = props;
    return (
        <IllustrationCard elevation="elevated" className={clsx('w-[112px] p-3', className)}>
            <div className="flex items-center gap-1.5">
                <Icon.LogoSquare
                    className="size-3.5 shrink-0"
                    style={{ color: 'var(--primary-solid)' }}
                />
                <WireframeBar className="h-1.5 w-1/2" />
            </div>
            <div className="mt-2.5 flex flex-col gap-1.5">
                <WireframeBar className="h-1 w-full opacity-60" />
                <WireframeBar className="h-1 w-5/6 opacity-60" />
                <WireframeBar className="h-1 w-full opacity-40" />
                <WireframeBar className="h-1 w-2/3 opacity-40" />
            </div>
        </IllustrationCard>
    );
}
